<template>
  <v-dialog
    persistent
    v-model="showWindow"
    scrollable
    max-width="1200"
    max-height="90%"
    min-width="90%"
  >
    <v-card height="100%" :loading="!fetched">
      <v-card-actions>
        <h3 class="pa-3 pt-1 pb-1">{{ $t("windowDiagnostic.title") }}</h3>
        <v-spacer></v-spacer>
        <v-btn icon @click="showWindow = false">
          <v-icon>fa-times</v-icon>
        </v-btn>
      </v-card-actions>
      <v-divider></v-divider>
      <v-card-text class="pt-5 pb-0 mb-0 px-3" v-if="diagnostic">
        <v-form ref="diagnosticForm">
          <v-data-table
            :height="'500px'"
            v-if="diagnostic.data"
            :first-day-of-week="first_day_of_week"
            :headers="diagnosticTableHeader"
            :items="diagnosticItems"
            :items-per-page="50"
            class="elevation-0"
            dense
            :hide-default-footer="true"
          >
            <template v-slot:item.type="{ item }">
              {{ $t("windowDiagnostic.parameters." + item.type) }}
            </template>
            <template v-slot:item.data.status="{ item }">
              <diagnostic-status-icons
                  @set-force-status="(forceStatus)=>setForceStatus(item, forceStatus)"
                  class="text-no-wrap"
                  :status="item.data.force_status || item.data.status"
              ></diagnostic-status-icons>
            </template>
            <template v-slot:item.replacements="{ item }">
              <v-simple-checkbox
                :disabled="isNowUpdatingTypes.includes(item.type)"
                :value="diagnostic.replacements[item.type]"
                @input="toggleReplacement(item.type)"
              ></v-simple-checkbox>
            </template>
            <template v-slot:item.regulations="{ item }">
              <template
                v-if="
                  item.type &&
                  regulationsParams[item.type]
                "
              >
                <v-text-field
                  prepend-icon="mdi-calendar"
                  @click:prepend="()=>{
                    datepickerDialog.isVisible = true;
                    datepickerDialog.item_type = item.type;
                    datepickerDialog.date = parseDate(
                      diagnostic.regulations[item.type]
                    );}
                  "
                  v-model="diagnostic.regulations[item.type]"
                  @focusout="updateDate(item)"
                  @keyup.enter.stop="updateDate(item)"
                  :rules="[v=>validateDateField(v) || $t('form.validationErrors.wrong_date')]"
                >
                </v-text-field>
              </template>
            </template>
            <template v-slot:item.data.comments="{ item }">
              <v-container fill-height fluid class="px-0 mx-0">
                <v-row align="center" justify="center" class="px-0 mx-0">
                  <v-col class="px-0 mx-0">
                    <template
                      v-if="
                        diagnostic.comments_custom &&
                        diagnostic.comments_custom[item.type]
                      "
                    >
                      {{ diagnostic.comments_custom[item.type] }}
                    </template>
                    <template v-else>
                      {{ item.data.comments && item.data.comments.join(", ") }}
                    </template>
                  </v-col>
                  <v-col class="px-0 mx-0">
                    <v-btn
                      elevation="0"
                      min-width="26"
                      height="26"
                      color="primary"
                      class="mb-0 ml-3 px-0 white--text"
                      :title="$t('common.comment')"
                      @click="showCommentDialog(item.type)"
                    >
                      <v-icon x-small>$pencil</v-icon>
                    </v-btn>
                  </v-col>
                </v-row>
              </v-container>
            </template>
            <template v-slot:item.brand="{ item }">
              <v-text-field
                :disabled="!editableBrandAndPrices.includes(item.type)"
                class="mt-3"
                v-model="diagnostic.brand[item.type]"
                :label="$t('windowDiagnostic.tableHeader.brand')"
              ></v-text-field>
            </template>
            <template v-slot:item.price="{ item }">
              <v-container fill-height fluid class="px-0 mx-0">
                <v-row align="center" justify="center" class="px-0 mx-0">
                  <v-col class="px-0 mx-0">
                    <v-text-field
                      :disabled="!editableBrandAndPrices.includes(item.type)"
                      :ref="'price_' + item.type"
                      class="mt-3"
                      :rules="[
                        (v) =>
                          v === null ||
                          v === '' ||
                          (v &&
                            v.replace(/,/g, '.') ==
                              parseFloat(v.replace(/,/g, '.'))) ||
                          $t('form.validationErrors.must_be_float'),
                      ]"
                      v-model="diagnostic.price[item.type]"
                      :label="$t('windowDiagnostic.tableHeader.price')"
                    ></v-text-field>
                  </v-col>
                  <v-col class="px-0 mx-0">
                    <v-btn
                      elevation="0"
                      min-width="26"
                      height="26"
                      color="primary"
                      class="mb-0 ml-3 px-0 white--text"
                      :title="$t('common.edit')"
                      @click="toggleAndSaveEditPriceAndBrand(item.type)"
                    >
                      <v-icon
                        x-small
                        v-if="!editableBrandAndPrices.includes(item.type)"
                        >$pencil</v-icon
                      >
                      <v-icon x-small v-else>fa-save</v-icon>
                    </v-btn>
                  </v-col>
                </v-row>
              </v-container>
            </template>
            <template v-slot:no-data>
              {{ $t("common.no_data_available") }}
            </template>
            <template v-slot:no-results>
              {{ $t("common.no_data_available") }}
            </template>
          </v-data-table>
        </v-form>
      </v-card-text>
      <v-card-text class="pt-5" v-else>
        <v-alert type="warning" outlined>
          {{ $t("windowDiagnostic.no_information") }}
          <v-tooltip right>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                  href="//mechanic.levam.net"
                  icon
                  target="_blank"
                  class="mr-1"
                  v-on="on"
              >
                <v-icon x-small>fa-cogs</v-icon>
              </v-btn>
            </template>
            <span
            >{{$t('common.mechanic_dashboard')}}</span
            >
          </v-tooltip>
        </v-alert>
      </v-card-text>
      <v-card-actions class="px-3">
        <v-spacer></v-spacer>
        <v-alert type="warning" outlined v-if="!isFilledTotally && diagnostic" dense>
          {{ $t("windowDiagnostic.not_filled_totally") }}
        </v-alert>
        <v-btn
            v-if="item && item.id && isFilledTotally"
            elevation="0"
            min-width="48"
            min-height="48"
            class=""
            :disabled="isDisabledPrint || !diagnostic"
            target="_blank"
            :href="`${$apiURL}/diagnostics/${item.id}/print`"
            :title="$t('common.print')"
        >
          <v-icon x-small>fa-print</v-icon>
        </v-btn>
      </v-card-actions>
    </v-card>
    <v-dialog
      ref="commentDialog"
      v-model="commentDialog.isVisible"
      persistent
      max-width="600px"
    >
      <v-card class="pt-6">
        <v-card-text>
          <v-textarea
            hide-details
            outlined
            :label="$t('common.comment')"
            v-model="commentDialog.text"
          ></v-textarea>
        </v-card-text>
        <v-divider class=""></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn elevation="0" @click="removeCustomComment">
            {{ $t("windowDiagnostic.rollback_to_autogenerated_comment") }}
          </v-btn>
          <v-btn
            class="white--text"
            elevation="0"
            color="red"
            @click="hideCommentDialog"
          >
            {{ $t("common.cancel") }}
          </v-btn>
          <v-btn color="primary" elevation="0" @click="saveCustomComment">
            {{ $t("common.save") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog
      ref="dialog"
      v-model="datepickerDialog.isVisible"
      persistent
      width="290px"
    >
      <v-date-picker v-model="datepickerDialog.date" scrollable :locale="locale">
        <v-spacer></v-spacer>
        <v-btn text color="primary" @click="clearDateFromDatepicker()">
          {{ $t("common.clear") }}
        </v-btn>
        <v-btn text color="primary" @click="datepickerDialog.isVisible = false">
          {{ $t("common.cancel") }}
        </v-btn>
        <v-btn text color="primary" @click="setDateFromDatepicker()">
          OK
        </v-btn>
      </v-date-picker>
    </v-dialog>
  </v-dialog>
</template>

<script>
import CommonMixin from "@/mixins/common";
import { mapFields } from "vuex-map-fields";
import DiagnosticStatusIcons from "@/components/order/DiagnosticStatusIcons";
import RightsMixin from "@/mixins/rights";

//Разделы в которых не должен выставляется регламент Срочно, а просто ставится галочка(PLAT-572):
const sections = [
    'engine_oil', //Масло в двигателе
    'brake_fluid', //Тормозная жидкость
    'rear_brake_discs', //Задние тормозные диски
    'rear_pads' //Задние колодки
];

export default {
  name: "DiagnosticWindow",
  components: { DiagnosticStatusIcons },
  mixins: [CommonMixin, RightsMixin],
  props: {
    show: false,
    item: null,
  },
  data() {
    return {
      editableBrandAndPrices: [],
      datepickerDialog: {
        isVisible: false,
        date: null,
        item_type: null,
      },
      commentDialog: {
        isVisible: false,
        item_type: null,
        text: null,
      },
      diagnosticTableHeader: [
        {
          text: this.$t("windowDiagnostic.tableHeader.element"),
          value: "type",
          sortable: false,
        },
        {
          text: this.$t("windowDiagnostic.tableHeader.status"),
          value: "data.status",
          sortable: false,
        },
        {
          text: this.$t("windowDiagnostic.tableHeader.replacements"),
          value: "replacements",
          sortable: false,
        },
        {
          text: this.$t("windowDiagnostic.tableHeader.regulations"),
          value: "regulations",
          width: "150px",
          sortable: false,
        },
        {
          text: this.$t("windowDiagnostic.tableHeader.comment"),
          value: "data.comments",
          sortable: false,
        },
        {
          text: this.$t("windowDiagnostic.tableHeader.brand"),
          value: "brand",
          width: "150px",
          sortable: false,
        },
        {
          text: this.$t("windowDiagnostic.tableHeader.price"),
          value: "price",
          width: "180px",
          sortable: false,
        },
      ],
    };
  },
  computed: {
    diagnosticItems(){
      return this.diagnostic.data && this.diagnostic.data.filter(diagnosticItem=>{
        let is_visible = true;
        this.diagnosticParameters.forEach(parameter=>{
          //скроем отключенные в настройках пункты
          if((diagnosticItem.type === parameter.type || diagnosticItem.splitted_parent === parameter.type) && parameter.config && parameter.config.is_hidden){
            is_visible = false;
          }
        })
        return is_visible;
      })
    },
    ...mapFields("site", ["first_day_of_week"]),
    ...mapFields("diagnostics", [
      "savingInProgress",
      "diagnostic",
      "diagnosticParameters",
      "fetched",
      "isNowUpdatingTypes",
      "regulationsParams",
    ]),

    isFilledTotally(){
      let isFilledTotally = true;
      if(this.diagnostic && this.diagnosticItems){
        this.diagnosticItems.forEach(item=>{
          if(!(item.data && item.data.status) && this.isOptionalParameter(item)===false) {
            isFilledTotally =  false;
          }
        })
      }
      return isFilledTotally;
    },
    isDisabledPrint(){
      if(!this.diagnostic || !this.isFilledTotally) return false;
    },
    showWindow: {
      get() {
        return this.show;
      },
      set() {
        this.$emit("hide");
      },
    },
    prefix() {
      return process.env.VUE_APP_IMG_PREFIX;
    },
  },
  methods: {
    isOptionalParameter(diagnosticItem){
      let is_optional = false;
      this.diagnosticParameters.forEach(parameter=>{
        this.diagnosticParameters.forEach(parameter=>{
          if((diagnosticItem.type === parameter.type || diagnosticItem.splitted_parent === parameter.type) && parameter.config && parameter.config.is_optional){
            is_optional = true;
          }
        })
      })
      return is_optional;
    },
    setForceStatus(item, forceStatus){
      if(this.userHasRight('change_diagnostic_statuses|full'))
      {
        item.data.force_status = forceStatus
        this.$store.dispatch("diagnostics/update", this.item.id);
      } else {
        let error = this.$t("right.errors.you_must_have_permission_x_to_perform_this_action", [this.$t('rightsNames.change_diagnostic_statuses')])
        this.$store.dispatch('common/showError', { error });
      }
    },
    removeCustomComment() {
      if (this.diagnostic && this.diagnostic.data) {
        let tmp = this.diagnostic.data.find(
          (el) => el.type === this.commentDialog.item_type
        );
        if (tmp && tmp.data && tmp.data.comments) {
          this.commentDialog.text = tmp.data.comments.join(", ");
        }
      }
    },
    saveCustomComment() {
      let autogeneratedCommentText;
      if (this.diagnostic && this.diagnostic.data) {
        let tmp = this.diagnostic.data.find(
          (el) => el.type === this.commentDialog.item_type
        );
        if (tmp && tmp.data && tmp.data.comments) {
          autogeneratedCommentText = tmp.data.comments.join(", ");
        }
      }
      if (autogeneratedCommentText === this.commentDialog.text) {
        this.diagnostic.comments_custom[this.commentDialog.item_type] = null;
      } else {
        this.diagnostic.comments_custom[this.commentDialog.item_type] =
          this.commentDialog.text;
      }
      this.hideCommentDialog();
      this.$store.dispatch("diagnostics/update", this.item.id);
    },
    hideCommentDialog() {
      this.commentDialog.isVisible = false;
      this.commentDialog.item_type = null;
      this.commentDialog.text = null;
    },
    /**
     * Делаем активными для реактирования поля бред и цена,
     * если они уже активны - деактивируем и сохраняем значение
     */
    toggleAndSaveEditPriceAndBrand(item_type) {
      if (!this.editableBrandAndPrices.includes(item_type)) {
        this.editableBrandAndPrices.push(item_type);
      } else {
        this.$refs.diagnosticForm.validate("price_" + item_type);
        if (this.$refs["price_" + item_type].hasError) {
          return;
        }
        this.editableBrandAndPrices.splice(
          this.editableBrandAndPrices.findIndex((o) => o === item_type),
          1
        );
        this.$store.dispatch("diagnostics/update", this.item.id);
      }
    },
    showCommentDialog(item_type) {
      if (
        this.diagnostic.comments_custom &&
        this.diagnostic.comments_custom[item_type]
      ) {
        this.commentDialog.text = this.diagnostic.comments_custom[item_type];
      } else {
        if (this.diagnostic && this.diagnostic.data) {
          let tmp = this.diagnostic.data.find((el) => el.type === item_type);
          if (tmp && tmp.data && tmp.data.comments) {
            this.commentDialog.text = tmp.data.comments.join(", ");
          }
        }
      }

      this.commentDialog.isVisible = true;
      this.commentDialog.item_type = item_type;
    },
    parseDate(str, format = "DD.MM.YYYY", locale = "ru") {
      let date;
      try {
        date = this.$moment(str, format, locale).format("YYYY-MM-DD");
      } catch (e) {
        date = this.$moment().format("YYYY-MM-DD");
      }
      if (!this.$moment(date).isValid()) {
        date = this.$moment().format("YYYY-MM-DD");
      }
      return date;
    },
    clearDateFromDatepicker(locale = "ru") {
      this.diagnostic.regulations[this.datepickerDialog.item_type] = null

      this.datepickerDialog.date = null;
      this.datepickerDialog.item_type = null;
      this.datepickerDialog.isVisible = false;
      this.$store.dispatch("diagnostics/update", this.item.id);
    },
    validateDateField(date){
      if(date===this.$t('windowDiagnostic.urgently') || date === null || date === '') return true;
      return this.validateShortDateString(date, 'ru')
    },
    updateDate(item){
      if( ! this.validateDateField(this.diagnostic.regulations[item.type])) return;
      this.$store.dispatch("diagnostics/update", this.item.id);

    },
    setDateFromDatepicker(locale = "ru") {
      this.$moment.locale(locale);
      this.diagnostic.regulations[this.datepickerDialog.item_type] =
        this.$moment(this.datepickerDialog.date).format("L");

      this.datepickerDialog.date = null;
      this.datepickerDialog.item_type = null;
      this.datepickerDialog.isVisible = false;
      this.$store.dispatch("diagnostics/update", this.item.id);
    },
    /**
     * Прибавляем к текущей дате days и форматируем согласно locale(ru - dd.mm.yyyy)
     */
    calcRegulationDate(days, locale = "ru") {
      this.$moment.locale(locale);
      return this.$moment().add(days, "days").format("L"); //28.06.2022
    },
    /**
     * Вычислим дату регламента
     */
    async calcRegulation(item_type, value, force = false) {
      if(value && sections.includes(item_type)){
        this.diagnostic.regulations[item_type] = '';
        return true;
      }

      if (value && this.diagnostic.regulations[item_type] === null) {
        //данные диагностики для item_type
        let data = this.diagnostic.data.find(
          (item) => item.type === item_type
        ).data;

        if (
          this.regulationsParams[item_type] &&
          this.regulationsParams[item_type].hasOwnProperty("condition")
        ) {
          if (!this.regulationsParams[item_type].condition) {
            this.diagnostic.regulations[item_type] = this.calcRegulationDate(
              this.regulationsParams[item_type].days
            );
          }
          if (this.regulationsParams[item_type].condition) {
            if (data) {
              //имя параметра условия
              let condition_parameter =
                this.regulationsParams[item_type].condition_parameter;
              //текущее значение параметра
              let current_parameter_value =
                data.parameters[condition_parameter].value;
              //количество дней в зависимости от current_parameter_value
              this.diagnostic.regulations[item_type] = this.calcRegulationDate(
                this.regulationsParams[item_type][current_parameter_value].days
              );
            } else {
              this.diagnostic.regulations[item_type] = null;
            }
          }
        } else {
          this.diagnostic.regulations[item_type] = null;
        }
        if (data.status === "alarm") {
          this.diagnostic.regulations[item_type] = this.$t(
            "windowDiagnostic.urgently"
          );
        }
      } else {
        this.diagnostic.regulations[item_type] = null;
      }
      return true;
    },
    toggleReplacement(item_type) {
      if (this.fetched) {
        this.diagnostic.replacements[item_type] =
          !this.diagnostic.replacements[item_type];
        this.calcRegulation(
          item_type,
          this.diagnostic.replacements[item_type]
        ).then(() => {
          this.$store.dispatch("diagnostics/update", this.item.id);
        });
      }
    },
  },
  watch: {
    show(to) {
      if (!to) {
        this.$emit("hide", false);
      } else {
        this.$store.dispatch("diagnostics/get", this.item.id);
        this.$store.dispatch("diagnostics/getParameters");
        //
      }
    },
  },
};
</script>

<style scoped></style>
